import { Component, computed, inject, input, signal } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  DokaTable,
  DokaThead,
  DokaTh,
  DokaActionMenu,
  DokaActionMenuItem,
  DokaTbody,
  DokaTd,
  DokaTr,
  DokaButton,
  DokaSearchField,
  DokaModalController,
} from '@doka/angular-core';
import { I18NextEagerPipe, I18NextService } from 'angular-i18next';
import { CatalogClient, CatalogItemTemplateModel } from 'src/api/services/generated/api-clients';
import { SpinnerComponent } from 'src/app/shared/components/spinner/spinner.component';
import { EmptyStateComponent } from 'src/app/shared/components/empty-state/empty-state.component';
import { CreateOrUpdateTemplateSlideOverComponent } from '../slide-overs/create-or-update-template-slide-over/create-or-update-template-slide-over.component';
import { ModalService } from 'src/app/shared/services/modal.service';
import { ToastService } from 'src/app/shared/services/toast.service';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-master-data-templates-grid',
  templateUrl: './templates-grid.component.html',
  imports: [
    DokaTable,
    DokaThead,
    DokaTh,
    DokaTr,
    DokaTbody,
    DokaTd,
    DokaActionMenu,
    DokaActionMenuItem,
    I18NextEagerPipe,
    DokaButton,
    DokaSearchField,
    SpinnerComponent,
    EmptyStateComponent,
  ],
})
export class TemplatesGridComponent {
  private readonly toastService = inject(ToastService);
  private readonly i18n = inject(I18NextService);
  private readonly modalService = inject(ModalService);
  private readonly modalCtrl = inject(DokaModalController);
  private readonly catalogClient = inject(CatalogClient);

  public readonly catalogItemId = input.required<string>();

  protected readonly templates = rxResource({
    params: () => this.catalogItemId(),
    stream: ({ params: id }) => this.catalogClient.getCatalogItemTemplatesByItem(id),
  });

  protected readonly catalogItem = rxResource({
    params: () => this.catalogItemId(),
    stream: ({ params: id }) => this.catalogClient.getCatalogItemById(id),
  });

  protected searchText = signal('');

  protected filteredTemplates = computed(() => {
    const searchTerm = this.searchText().toLowerCase();
    return (
      this.templates.value()?.data?.filter((template) => template.country.toLowerCase().includes(searchTerm)) ?? []
    );
  });

  protected async openTemplatesSlideOver(template?: CatalogItemTemplateModel) {
    const catalogItem =
      this.catalogItem.value()?.data ?? (await firstValueFrom(this.catalogClient.getCatalogItemById(this.catalogItemId()))).data;

    if (!catalogItem) {
      return;
    }

    const modal = await this.modalCtrl.create(
      {
        component: CreateOrUpdateTemplateSlideOverComponent,
        componentProps: {
          itemTemplate: signal(template).asReadonly(),
          catalogItem: signal(catalogItem).asReadonly(),
        },
      },
      'slide-over',
    );

    await modal.present();

    await this.onModalDismiss(modal);
  }

  protected async openDeleteTemplateModal(template: CatalogItemTemplateModel) {
    const decision = await this.modalService.openDeleteModal(
      this.i18n.t('DeleteCatalogItemTemplate.Content', {
        itemName: this.catalogItem.value()?.data?.name,
        country: template.country,
      }),
      this.i18n.t('DeleteCatalogItemTemplate.Title'),
    );

    if (decision) {
      try {
        await firstValueFrom(this.catalogClient.deleteCatalogItemTemplate(template.id));
        await this.toastService.showSuccess(
          this.i18n.t('Common.SuccessDeleteToast', {
            itemName: `Template in ${template.country} of ${this.catalogItem.value()?.data?.name}`,
          }),
        );
        this.templates.reload();
      } catch {
        await this.toastService.showError(
          this.i18n.t('Common.ErrorDeleteToast', {
            itemName: `Template in ${template.country} of ${this.catalogItem.value()?.data?.name}`,
          }),
        );
      }
    }
  }

  private async onModalDismiss(modal: HTMLIonModalElement) {
    const { data, role } = await modal.onDidDismiss();
    if (data && role === 'created') {
      this.templates.reload();
    }
  }
}
